import { HTMLAttributes, useEffect, useState } from 'react';
import { apiReadAllNotification, apiReadNotification } from '@/dashboard/api/settings';
import { ReactComponent as ConfirmIcon } from '@/dashboard/assets/images/settings/img_setting_confirm.svg';
import NotificationsCardView from '@/dashboard/components/Setting/notifications/components/card';
import LogoLoading from '@/dashboard/components/loading/LogoLoading';
import { useMaterialMedia } from '@/dashboard/hooks/useMaterialMedia';
import { useProjectNavigate } from '@/dashboard/hooks/useProjectNavigate';
import { Divider } from '@/dashboard/mui';
import { useSettingsStore } from '@/dashboard/store/SettingsStore';
import { getSettingNotificationsPath } from '@/dashboard/utils/routes';
import { BellIcon } from '@heroicons/react/outline';
import { Close } from '@mui/icons-material';
import { Badge, Box, Button, IconButton, Menu, Typography } from '@mui/material';
import clsx from 'clsx';

type UnreadNotificationDropDownMenuProps = HTMLAttributes<HTMLDivElement>;

const UnreadNotificationDropDownMenu = (props: UnreadNotificationDropDownMenuProps) => {
  const { className } = props;
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [{ unReadNotificationList }, { updateUnReadNotificationList }] = useSettingsStore((store) => [store.state, store.actions]);
  const { isMobile } = useMaterialMedia();
  const [loading, setLoading] = useState(false);
  const { push } = useProjectNavigate();

  /**
   * requests
   */
  // request read all
  const requestReadAll = async () => {
    setLoading(true);
    const [res] = await apiReadAllNotification({});
    setLoading(false);

    if (res) {
      updateUnReadNotificationList();
    }
  };

  /**
   * effects
   */
  useEffect(() => {
    updateUnReadNotificationList();
  }, []);

  /**
   * methods
   */
  const clickAction = (e) => {
    setAnchorEl(e.currentTarget);
    updateUnReadNotificationList();
  };

  // close action
  const closeAction = () => {
    setAnchorEl(null);
  };

  // go to notification page
  const notificationClickAction = () => {
    push({ path: getSettingNotificationsPath() });
  };

  // notification card click
  const cardClickAction = async (item) => {
    await apiReadNotification({ notification_id: item.id });
    updateUnReadNotificationList();
  };

  const open = !!anchorEl;
  return (
    <Box className={clsx(className)}>
      <Badge badgeContent={unReadNotificationList.length} color="error">
        <BellIcon className="h-7 w-7 cursor-pointer" onClick={clickAction} />
      </Badge>
      <Menu
        anchorEl={anchorEl}
        id="account-user-menu"
        open={open}
        onClose={closeAction}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              overflow: 'visible',
              filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
              mt: 1.5,
              '& .MuiAvatar-root': {
                width: 32,
                height: 32,
                ml: -0.5,
                mr: 1,
              },
              '&::before': {
                content: '""',
                display: 'block',
                position: 'absolute',
                top: 0,
                right: 14,
                width: 10,
                height: 10,
                bgcolor: 'background.paper',
                transform: 'translateY(-50%) rotate(45deg)',
                zIndex: 0,
              },
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box
          className={clsx('flex max-h-[500px] flex-col', {
            'w-[460px]': !isMobile,
            'w-full min-w-[260px]': isMobile,
          })}
        >
          <Box className="flex h-14 flex-row items-center justify-between px-4">
            <Typography className="font-bold" variant="h6">
              Notifications
            </Typography>
            <Box className="flex flex-row items-center">
              {unReadNotificationList.length > 0 && (
                <Button
                  className="text-brand-primary text-sm font-bold"
                  variant="text"
                  startIcon={<ConfirmIcon className="h-[10px] w-[20px]" />}
                  onClick={requestReadAll}
                >
                  Mark all as read
                </Button>
              )}

              <IconButton onClick={closeAction}>
                <Close className="h-6 w-6" />
              </IconButton>
            </Box>
          </Box>
          <Box className="flex flex-col overflow-auto">
            {loading && <LogoLoading />}
            {unReadNotificationList.map((item, index) => {
              return <NotificationsCardView className="p-5" key={index} model={item} onClick={() => cardClickAction(item)} />;
            })}
          </Box>
          <Divider />
          <Box className="flex h-10 items-center justify-center">
            <Button className="text-black-secondary mt-2 text-base font-bold" variant="text" onClick={notificationClickAction}>
              View all notifications
            </Button>
          </Box>
        </Box>
      </Menu>
    </Box>
  );
};

export default UnreadNotificationDropDownMenu;
